import { makeApiRequest, generateSymbol, parseFullSymbol } from "./helpers.js";
import { subscribeOnStream, unsubscribeFromStream } from "./streaming.js";

const lastBarsCache = new Map();

// DatafeedConfiguration implementation
const configurationData = {
  // Represents the resolutions for bars supported by your datafeed
  supported_resolutions: ["1D", "1W", "1M"],
  // The `exchanges` arguments are used for the `searchSymbols` method if a user selects the exchange
  exchanges: [
    {
      value: "Bitfinex",
      name: "Bitfinex",
      desc: "Bitfinex",
    },
    {
      value: "Kraken",
      // Filter name
      name: "Kraken",
      // Full exchange name displayed in the filter popup
      desc: "Kraken bitcoin exchange",
    },
  ],
  // The `symbols_types` arguments are used for the `searchSymbols` method if a user selects this symbol type
  symbols_types: [
    {
      name: "crypto",
      value: "crypto",
    },
  ],
};

// Obtains all symbols for all exchanges supported by CryptoCompare API
async function getAllSymbols() {
  const data = await makeApiRequest("data/v3/all/exchanges");
  let allSymbols = [];

  for (const exchange of configurationData.exchanges) {
    const pairs = data.Data[exchange.value].pairs;

    for (const leftPairPart of Object.keys(pairs)) {
      const symbols = pairs[leftPairPart].map((rightPairPart) => {
        const symbol = generateSymbol(
          exchange.value,
          leftPairPart,
          rightPairPart
        );
        return {
          symbol: symbol.short,
          full_name: symbol.full,
          description: symbol.short,
          exchange: exchange.value,
          type: "crypto",
        };
      });
      allSymbols = [...allSymbols, ...symbols];
    }
  }
  return allSymbols;
}

export default {
  onReady: (callback) => {
    console.log("[onReady]: Method call");
    setTimeout(() => callback(configurationData));
  },

  searchSymbols: async (
    userInput,
    exchange,
    symbolType,
    onResultReadyCallback
  ) => {
    console.log("[searchSymbols]: Method call");
    const symbols = await getAllSymbols();
    const newSymbols = symbols.filter((symbol) => {
      const isExchangeValid = exchange === "" || symbol.exchange === exchange;
      const isFullSymbolContainsInput =
        symbol.full_name.toLowerCase().indexOf(userInput.toLowerCase()) !== -1;
      return isExchangeValid && isFullSymbolContainsInput;
    });
    onResultReadyCallback(newSymbols);
  },

  resolveSymbol: async (
    symbolName,
    onSymbolResolvedCallback,
    onResolveErrorCallback,
    extension
  ) => {
    console.log("[resolveSymbol]: Method call", symbolName);
    const symbols = await getAllSymbols();

    // First try to find the exact match
    let symbolItem = symbols.find(({ full_name }) => full_name === symbolName);

    // If not found, try to match only by short name
    if (!symbolItem) {
      const parsedSymbol = parseFullSymbol(symbolName);
      if (parsedSymbol) {
        symbolItem = symbols.find(
          (s) =>
            s.symbol === `${parsedSymbol.fromSymbol}/${parsedSymbol.toSymbol}`
        );
      }
    }

    if (!symbolItem) {
      console.log("[resolveSymbol]: Cannot resolve symbol", symbolName);
      onResolveErrorCallback("cannot resolve symbol");
      return;
    }

    // Symbol information object
    const symbolInfo = {
      ticker: symbolItem.full_name,
      name: symbolItem.symbol,
      full_name: symbolItem.full_name, // Make sure full_name is consistent
      description: symbolItem.description,
      type: symbolItem.type,
      session: "24x7",
      timezone: "Etc/UTC",
      exchange: symbolItem.exchange,
      minmov: 1,
      pricescale: 100,
      has_intraday: false,
      has_no_volume: true,
      has_weekly_and_monthly: false,
      supported_resolutions: configurationData.supported_resolutions,
      volume_precision: 2,
      data_status: "streaming",
    };

    console.log("[resolveSymbol]: Symbol resolved", symbolName);
    onSymbolResolvedCallback(symbolInfo);
  },

  // In the getBars function, enhance error handling and logging:
  getBars: async (
    symbolInfo,
    resolution,
    periodParams,
    onHistoryCallback,
    onErrorCallback
  ) => {
    const { from, to, firstDataRequest } = periodParams;
    console.log("[getBars]: Method call", symbolInfo, resolution, from, to);

    try {
      // Use a consistent key for the symbol throughout your code
      const symbolKey =
        symbolInfo.full_name || symbolInfo.ticker || symbolInfo.name;
      const parsedSymbol = parseFullSymbol(symbolKey);

      if (!parsedSymbol) {
        console.error("[getBars]: Failed to parse symbol", symbolInfo);
        onErrorCallback("Failed to parse symbol");
        return;
      }

      console.log("[getBars]: Using symbol", parsedSymbol);

      const urlParameters = {
        e: parsedSymbol.exchange,
        fsym: parsedSymbol.fromSymbol,
        tsym: parsedSymbol.toSymbol,
        toTs: to,
        limit: 2000,
      };

      const query = Object.keys(urlParameters)
        .map((name) => `${name}=${encodeURIComponent(urlParameters[name])}`)
        .join("&");

      console.log("[getBars]: API Request", `data/v2/histoday?${query}`);

      const data = await makeApiRequest(`data/v2/histoday?${query}`);
      console.log("[getBars]: Received data", data);

      if (
        (data.Response && data.Response === "Error") ||
        !data.Data ||
        !data.Data.Data ||
        data.Data.Data.length === 0
      ) {
        console.warn("[getBars]: No data or error in response", data);
        onHistoryCallback([], { noData: true });
        return;
      }

      let bars = [];
      // Debug the filtering
      console.log(`[getBars]: Filtering data from=${from}, to=${to}`);
      console.log(`[getBars]: Total data points: ${data.Data.Data.length}`);

      // Check time ranges before filtering
      const timeRange = data.Data.Data.map((bar) => bar.time);
      console.log(
        `[getBars]: Time range in data: min=${Math.min(
          ...timeRange
        )}, max=${Math.max(...timeRange)}`
      );

      data.Data.Data.forEach((bar) => {
        if (bar.time >= from && bar.time < to) {
          bars.push({
            time: bar.time * 1000, // Convert to milliseconds
            low: bar.low,
            high: bar.high,
            open: bar.open,
            close: bar.close,
          });
        }
      });

      console.log(`[getBars]: Filtered ${bars.length} bars`);

      if (bars.length === 0) {
        // Debug why no bars were found
        console.warn("[getBars]: No bars match time range");
        onHistoryCallback([], { noData: true });
        return;
      }

      if (firstDataRequest && bars.length > 0) {
        // Make sure to use the same key consistently
        lastBarsCache.set(symbolKey, { ...bars[bars.length - 1] });
        console.log(
          `[getBars]: Set last bar cache for ${symbolKey}`,
          lastBarsCache.get(symbolKey)
        );
      }

      console.log(`[getBars]: Returning ${bars.length} bar(s)`);
      onHistoryCallback(bars, { noData: false });
    } catch (error) {
      console.error("[getBars]: Error", error);
      onErrorCallback(error);
    }
  },
  subscribeBars: (
    symbolInfo,
    resolution,
    onRealtimeCallback,
    subscriberUID,
    onResetCacheNeededCallback
  ) => {
    console.log(
      "[subscribeBars]: Method call with subscriberUID:",
      subscriberUID
    );
    subscribeOnStream(
      symbolInfo,
      resolution,
      onRealtimeCallback,
      subscriberUID,
      onResetCacheNeededCallback,
      lastBarsCache.get(symbolInfo.full_name)
    );
  },

  unsubscribeBars: (subscriberUID) => {
    console.log(
      "[unsubscribeBars]: Method call with subscriberUID:",
      subscriberUID
    );
    unsubscribeFromStream(subscriberUID);
  },
};
